import { useState } from "react";
import { Link } from "react-router-dom";
import { FormRadio } from "../../components";
import { validateEmail, formatName } from "../../utils/constants"; // helpers stay local
import useConfigOptions from "../../hooks/useConfigOptions";
import { postRequest } from "../../Requests/postRequests";
import { useDispatch } from "react-redux";
import { show } from "../../slices/toastSlice";
import Logo from "../../assets/images/logo.png";

function Register() {

    const dispatch = useDispatch();
    const { adminRolesSelect } = useConfigOptions();
    const [first_name, setFirst_Name] = useState("");
    const [last_name, setLast_Name] = useState("");
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [confirm_password, setConfirm_Password] = useState("");
    const [phone_number, setPhone_Number] = useState("");
    const [roles, setRoles] = useState([]);
    const [process, setProcess] = useState(false);

    const handleRequest = async (e) => {
        e.preventDefault();
        setProcess(true);

        if (!first_name || !last_name || first_name.length < 3 || last_name.length < 3) {
            dispatch(show({ type: "error", state: true, message: "Kindly provide a valid name" }));
            setProcess(false);
            return;
        }

        if (!email || !validateEmail(email)) {
            dispatch(show({ type: "error", state: true, message: "Kindly provide a valid email" }));
            setProcess(false);
            return;
        }

        if (confirm_password !== password) {
            dispatch(show({ type: "error", state: true, message: "Passwords do not match" }));
            setProcess(false);
            return;
        }

        if (phone_number.length !== 10) {
            dispatch(show({ type: "error", state: true, message: "Phone number must be 10 digits" }));
            setProcess(false);
            return;
        }

        if (roles.length < 1) {
            dispatch(show({ type: "error", state: true, message: "Kindly select a role" }));
            setProcess(false);
            return;
        }

        const res = await postRequest(`/auth/staff/register`, {
            otherNames: formatName(first_name),
            surname: formatName(last_name),
            email,
            password,
            phone: phone_number,
            roles,
        });

        setProcess(false);

        if (res?.err) {
            dispatch(show({ type: "error", state: true, message: res.err }));
            return;
        }

        dispatch(show({ type: "success", state: true, message: "An admin will confirm your submission" }));
    };

    return (
        <div className="min-h-screen bg-slate-50 dark:bg-gray-950 flex items-center justify-center p-4 py-12">
            <div className="w-full max-w-lg">

                {/* Logo */}
                <div className="flex justify-center mb-8">
                    <img src={Logo} alt="CedisPay" className="h-10 w-auto object-contain" />
                </div>

                {/* Card */}
                <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8">

                    {/* Heading */}
                    <div className="mb-7">
                        <h1 className="text-2xl font-semibold tracking-tight text-gray-900 dark:text-white">
                            Create an account
                        </h1>
                        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                            Fill in your details — an admin will review and approve your account
                        </p>
                    </div>

                    <form onSubmit={handleRequest} className="space-y-5">

                        {/* Name row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="first-name" className="form-label">First name</label>
                                <input
                                    type="text"
                                    id="first-name"
                                    name="first-name"
                                    autoComplete="given-name"
                                    required
                                    onChange={(e) => setFirst_Name(e.target.value)}
                                    className="form-input"
                                    placeholder="John"
                                />
                            </div>
                            <div>
                                <label htmlFor="last-name" className="form-label">Last name</label>
                                <input
                                    type="text"
                                    id="last-name"
                                    name="last-name"
                                    autoComplete="family-name"
                                    required
                                    onChange={(e) => setLast_Name(e.target.value)}
                                    className="form-input"
                                    placeholder="Doe"
                                />
                            </div>
                        </div>

                        {/* Email + Phone row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="email" className="form-label">Email address</label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    autoComplete="email"
                                    required
                                    onChange={(e) => setEmail(e.target.value)}
                                    className="form-input"
                                    placeholder="you@cedispay.com"
                                />
                            </div>
                            <div>
                                <label htmlFor="phone-number" className="form-label">Phone number</label>
                                <input
                                    type="text"
                                    id="phone-number"
                                    name="phone-number"
                                    required
                                    onChange={(e) => setPhone_Number(e.target.value)}
                                    className="form-input"
                                    placeholder="0241112345"
                                />
                            </div>
                        </div>

                        {/* Password row */}
                        <div className="grid grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="password" className="form-label">Password</label>
                                <input
                                    type="password"
                                    id="password"
                                    name="password"
                                    required
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="form-input"
                                    placeholder="••••••••"
                                />
                            </div>
                            <div>
                                <label htmlFor="confirm-password" className="form-label">Confirm password</label>
                                <input
                                    type="password"
                                    id="confirm-password"
                                    name="confirm-password"
                                    required
                                    onChange={(e) => setConfirm_Password(e.target.value)}
                                    className="form-input"
                                    placeholder="••••••••"
                                />
                            </div>
                        </div>

                        {/* Roles */}
                        <div className="pt-1">
                            <FormRadio
                                label="Select role"
                                type="checkbox"
                                content={adminRolesSelect}
                                value={roles}
                                setValue={setRoles}
                            />
                        </div>

                        {/* Divider */}
                        <div className="border-t border-gray-100 dark:border-gray-800 pt-2" />

                        <button
                            type="submit"
                            disabled={process}
                            className="w-full h-11 flex items-center justify-center rounded-xl bg-light-blue hover:bg-dark-blue text-white text-sm font-semibold transition-colors disabled:opacity-60 disabled:cursor-not-allowed"
                        >
                            {process ? (
                                <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                                </svg>
                            ) : (
                                "Create account"
                            )}
                        </button>
                    </form>
                </div>

                {/* Footer */}
                <p className="mt-5 text-center text-sm text-gray-500 dark:text-gray-400">
                    Already have an account?{" "}
                    <Link to="/login" className="text-blue-600 dark:text-blue-400 font-medium hover:underline">
                        Sign in
                    </Link>
                </p>

            </div>
        </div>
    );
}

export default Register;
